import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Download } from "lucide-react";

const links = [
    { href: "#work", label: "Work" },
    { href: "#about", label: "About" },
    { href: "#skills", label: "Skills" },
    { href: "#experience", label: "Experience" },
    { href: "#contact", label: "Contact" },
];

export default function Navbar({ profile, settings }) {
    const [scrolled, setScrolled] = useState(false);
    const [activeSection, setActiveSection] = useState("");
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => {
            setScrolled(window.scrollY > 40);

            // ScrollSpy for sections
            if (location.pathname === "/") {
                const sectionIds = ["work", "about", "skills", "experience", "contact"];
                const scrollPos = window.scrollY + 180;
                let current = "";
                for (let i = sectionIds.length - 1; i >= 0; i--) {
                    const el = document.getElementById(sectionIds[i]);
                    if (el && el.offsetTop <= scrollPos) {
                        current = `#${sectionIds[i]}`;
                        break;
                    }
                }
                setActiveSection(current);
            }
        };

        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, [location.pathname]);

    const handleAnchor = (e, href) => {
        if (location.pathname !== "/") {
            // Not on home page, let the standard link redirect to `/${href}`
            setOpen(false);
            return;
        }
        e.preventDefault();
        document.querySelector(href)?.scrollIntoView({ behavior: "smooth", block: "start" });
        setOpen(false);
    };

    return (
        <header
            data-testid="public-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled ? "bg-black/75 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="nav-logo"
                    className="flex items-center gap-3 group"
                    onClick={() => {
                        if (location.pathname === "/") {
                            window.scrollTo({ top: 0, behavior: "smooth" });
                        }
                    }}
                >
                    <div className="relative w-9 h-9 rounded-md bg-gradient-to-br from-brand to-brand-hover flex items-center justify-center overflow-hidden shadow-[0_0_15px_rgba(255,69,0,0.3)]">
                        {settings?.logo_url ? (
                            <img src={settings.logo_url} alt="Logo" className="w-full h-full object-cover relative z-10" />
                        ) : (
                            <span className="font-display text-lg text-white relative z-10">
                                {profile?.name?.[0] || "A"}
                            </span>
                        )}
                        <span className="absolute -inset-1 rounded-md bg-brand/30 blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="leading-tight">
                        <div className="font-display text-sm text-white">{profile?.name || "Akhmad Ferdiansyah"}</div>
                        <div className="mono-label line-clamp-1 text-xs tracking-wider">
                            {profile?.title || "Full Stack Developer & AI Specialist"}
                        </div>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-8">
                    {links.map((l) => {
                        const isActive = location.pathname === "/" && activeSection === l.href;
                        const targetHref = location.pathname === "/" ? l.href : `/${l.href}`;
                        return (
                            <a
                                key={l.href}
                                href={targetHref}
                                data-testid={`nav-link-${l.label.toLowerCase()}`}
                                onClick={(e) => handleAnchor(e, l.href)}
                                className={`relative py-1 text-sm transition-all duration-200 ${
                                    isActive ? "text-white font-medium" : "text-ink-200 hover:text-white"
                                }`}
                            >
                                {l.label}
                                {isActive && (
                                    <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-brand rounded-full shadow-[0_0_8px_rgba(255,69,0,0.8)] animate-in fade-in duration-200" />
                                )}
                            </a>
                        );
                    })}
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    {profile?.resume_url && (
                        <a
                            href={profile.resume_url}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="nav-resume"
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 text-white text-sm hover:bg-white/5 transition-colors"
                        >
                            <Download size={14} /> Resume
                        </a>
                    )}
                    <a
                        href={location.pathname === "/" ? "#contact" : "/#contact"}
                        data-testid="nav-cta"
                        onClick={(e) => handleAnchor(e, "#contact")}
                        className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-black text-sm font-medium hover:bg-brand hover:text-white transition-colors"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-brand group-hover:bg-white" />
                        Get in touch
                    </a>
                </div>

                <button
                    aria-label="Open menu"
                    data-testid="nav-mobile-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="md:hidden w-10 h-10 inline-flex items-center justify-center rounded-md border border-white/10 text-white"
                >
                    {open ? <X size={18} /> : <Menu size={18} />}
                </button>
            </div>

            {open && (
                <div className="md:hidden border-t border-white/5 bg-black/95 backdrop-blur-xl">
                    <div className="px-6 py-6 flex flex-col gap-4">
                        {links.map((l) => {
                            const isActive = location.pathname === "/" && activeSection === l.href;
                            const targetHref = location.pathname === "/" ? l.href : `/${l.href}`;
                            return (
                                <a
                                    key={l.href}
                                    href={targetHref}
                                    onClick={(e) => handleAnchor(e, l.href)}
                                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                    className={`text-base flex items-center justify-between py-1 transition-colors ${
                                        isActive ? "text-white font-medium" : "text-ink-200"
                                    }`}
                                >
                                    <span>{l.label}</span>
                                    {isActive && <span className="w-1.5 h-1.5 rounded-full bg-brand" />}
                                </a>
                            );
                        })}
                        <a
                            href={location.pathname === "/" ? "#contact" : "/#contact"}
                            onClick={(e) => handleAnchor(e, "#contact")}
                            className="mt-2 inline-flex items-center justify-center px-5 py-3 rounded-full bg-brand text-white text-sm font-medium"
                        >
                            Get in touch
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
